import { saveAndApplySourcingRequirement } from "@/apis/requirementManagement.api";
import {
  SaveAndApplySourcingRequirementSchema,
  SourcingNodeListItemSchema,
  SrmSourcingApplicationItemDetail,
} from "@/types/apis";
import { useMutation } from "@tanstack/react-query";
import { FormInstance, Space } from "antd";
import type { SourcingRequirementApplicationFormFields } from "../type";
import { factoryOptions } from "@/constants";
import {
  PrimaryButton,
  PrimaryOutlineButton,
} from "@/components/ui/buttons/PrimaryButton";
import { useModal } from "@/hooks/modals/useModal";
import { notifySuccess } from "@/utils/notification";
import {
  ApprovalForm,
  type ApprovalFormFields,
} from "@/components/ApprovalForm/ApprovalForm";
import { useFlow, useNowFlowNode } from "@/hooks/services/useFlow";
import { useSourcingRequirementQuery } from "./useSourcingRequirementQuery";
import dayjs from "dayjs";

const makeSubmitParams = (
  values: SourcingRequirementApplicationFormFields,
): Partial<SaveAndApplySourcingRequirementSchema> => {
  return {
    sourcingType: values.sourcingType,
    subSourcingType: values.subSourcingType,
    factoryValue: values.factoryValue,
    costCenter: values.costCenter,
    factory: factoryOptions.find(
      (option) => option.value === values.factoryValue,
    )?.label,
    standardMaterialFiles: values.standardMaterialFiles
      ?.map((file) => file.url)
      .join(),
    coaFiles: values.coaFiles?.map((file) => file.url).join(),
    ursFiles: values.ursFiles?.map((file) => file.url).join(),
    othersFiles: values.othersFiles?.map((file) => file.url).join(),
    srmSourcingMaterialList: values.materialList.map((item) => {
      return {
        materialName: item.materialName,
        materialCode: item.materialCode,
        annualQuantity: item.count,
        relatedProducts: item.products,
        completionDate: item.completeDate?.format("YYYY-MM-DD"),
        sourcingReason: item.sourcingReason,
        sourcingRequirements: item.sourcingRequirements,
        sourcingPrioritize: item.sourcingPrioritize,
        sourcingOrigin: "1", // 寻源来源-计划内
      };
    }),
  };
};

export function useFormFooter(
  form: FormInstance<SourcingRequirementApplicationFormFields>,
  cleanup: () => void,
) {
  const { props } = useModal("SOURCING_APPLICATION_20251202");

  const { recordId, onSuccess, mode, taskId, viewStatus, isReadOnly } = props;

  const detail = useSourcingRequirementQuery(recordId);

  const nodeCode = useNowFlowNode(detail?.insId);

  /**
   * 当前流程节点为 ra manager
   */
  const isRaApprove = nodeCode === "RA_MANAGER";

  /**
   * 当前流程节点为 qc manager
   */
  const standardNodeCodes = [
    "QC_MANAGER",
    "e4433b6b-358f-4e27-99ec-3cdd7ab8976c",
  ];
  const isQcApprove = Boolean(nodeCode && standardNodeCodes.includes(nodeCode));

  const { passFlowMutation } = useFlow("SOURCING_APPLICATION_20251202", false);

  // 保存并申请
  const saveMutation = useMutation({
    mutationFn: (data: Partial<SaveAndApplySourcingRequirementSchema>) =>
      saveAndApplySourcingRequirement(data),
    onSuccess() {
      onSuccess?.();
      notifySuccess("提交成功");
      cleanup();
    },
  });

  // 暂存
  const saveDraft = () => {
    const values = form.getFieldsValue();

    saveMutation.mutate({
      ...makeSubmitParams(values),
      id: props.recordId,
      reviewStatus: 1,
    });
  };

  // 提交
  const submit = async () => {
    const values = await form.validateFields();

    const params = makeSubmitParams(values);

    // 添加默认寻源进度节点列表
    params.srmSourcingMaterialList = params.srmSourcingMaterialList?.map(
      (m) => {
        return {
          ...m,
          sourcingNodeList: makeDefaultSourcingNodeList(values.sourcingType),
        };
      },
    );

    saveMutation.mutate({
      ...params,
      id: recordId,
      reviewStatus: 2,
    });
  };

  // ra 审批
  const raApprove = async () => {
    if (!taskId) return;
    const values = await form.validateFields();
    const isReject = values.formulaChange === 1;
    passFlowMutation.mutate(
      {
        taskId,
        flowParamsDto: {
          skipType: isReject ? "" : "PASS", // 驳回时传空处理
          message: isReject ? "寻源物料产品正在处方变更" : "",
          flowCode: "SOURCING_APPLICATION_20251202",
          variable: {
            formulaChange: values.formulaChange,
            changedTime: isReject
              ? dayjs(values.changedTime).format("YYYY-MM-DD")
              : null,
          },
        },
      },
      {
        onSuccess() {
          cleanup();
          onSuccess?.();
        },
      },
    );
  };

  // qc 审批
  const qcApprove = async (approveValues: ApprovalFormFields) => {
    if (!taskId) return;
    const values = await form.validateFields();
    const isStandardChanged = values.standardChanged === 0;

    passFlowMutation.mutate(
      {
        taskId,
        flowParamsDto: {
          skipType: approveValues.skipType === "REJECT" ? "" : "PASS", // 驳回时传空处理
          message: approveValues.message,
          flowCode: "SOURCING_APPLICATION_20251202",
          variable: {
            standardChanged: values.standardChanged,
            newStandardFiles: isStandardChanged
              ? values.newStandardFiles?.map((file) => file.url).join()
              : null,
          },
        },
      },
      {
        onSuccess() {
          cleanup();
          onSuccess?.();
        },
      },
    );
  };

  // 审批
  const approve = async (values: ApprovalFormFields) => {
    if (!taskId) return;
    passFlowMutation.mutate(
      {
        taskId,
        flowParamsDto: {
          skipType: values.skipType === "REJECT" ? "" : "PASS", // 驳回时传空处理
          message: values.message,
          flowCode: "SOURCING_APPLICATION_20251202",
          variable: {},
        },
      },
      {
        onSuccess() {
          cleanup();
          onSuccess?.();
        },
      },
    );
  };

  const renderFooter = (detail?: SrmSourcingApplicationItemDetail) => {
    const reviewStatus = detail?.reviewStatus;

    const Footer = {
      edit: (
        <Space>
          <PrimaryOutlineButton hidden={isRaApprove} onClick={saveDraft}>
            暂存
          </PrimaryOutlineButton>
          <PrimaryButton onClick={isRaApprove ? raApprove : submit}>
            提交
          </PrimaryButton>
        </Space>
      ),
      approve: (
        <ApprovalForm onApprovalSubmit={isQcApprove ? qcApprove : approve} />
      ),
      close: (
        <Space>
          <PrimaryOutlineButton onClick={cleanup}>关闭</PrimaryOutlineButton>
        </Space>
      ),
    };

    const closeRules = [
      mode === "started" && reviewStatus === 2, // 申请人-审批中
      mode === "started" && reviewStatus === 3 && isReadOnly, // 申请人-审批驳回
      mode === "started" && reviewStatus === 4, // 申请人-审批通过
      mode === "inReview" && viewStatus === false, // 审批人-审批任务待办已处理
    ];

    if (closeRules.some((r) => r)) {
      return Footer.close;
    }

    // 用户收到申请并审批(待办任务)
    if (
      mode === "inReview" &&
      reviewStatus === 2 &&
      viewStatus === true &&
      !isRaApprove
    ) {
      return Footer.approve;
    }

    return Footer.edit;
  };

  return renderFooter;
}

function makeDefaultSourcingNodeList(sourcingType: string) {
  const defaultProductionList: SourcingNodeListItemSchema[] = [
    {
      nodeCode: "M1",
      nodeName: "物料质量标准确认",
    },
    {
      nodeCode: "M2",
      nodeName: "供应商开发和筛选",
    },
    {
      nodeCode: "M3",
      nodeName: "供应商选择和评估",
    },
    {
      nodeCode: "M4",
      nodeName: "样品小试或中试研究",
    },
    {
      nodeCode: "M5",
      nodeName: "供应商合格认证（原供应商准入）",
    },
  ];

  const defaultNonProductionList: SourcingNodeListItemSchema[] = [
    {
      nodeCode: "M1",
      nodeName: "提供URS",
    },
    {
      nodeCode: "M2",
      nodeName: "供应商开发和筛选",
    },
    {
      nodeCode: "M3",
      nodeName: "用户与供应商技术沟通",
    },
    {
      nodeCode: "M4",
      nodeName: "采购与供应商商务沟通",
    },
    {
      nodeCode: "M5",
      nodeName: "汇总技术与商务资料",
    },
    {
      nodeCode: "M6",
      nodeName: "采购委员会获批",
    },
    {
      nodeCode: "M7",
      nodeName: "合同签署获批",
    },
  ];

  if (sourcingType.toString() === "1") return defaultProductionList;

  return defaultNonProductionList;
}
